import { useState, useEffect, useMemo } from 'react';
import { usePageTitle } from '@/hooks/use-page-title';
import { useAuth } from '@/lib/auth';
import { usePermissions } from '@/hooks/use-permissions';
import { Permissions } from '@/lib/permissions';
import { useHeaderAction } from '@/lib/header-action-context';
import { useConnectStatus } from '@/hooks/use-connect-status';
import { api } from '@/lib/api-client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ConnectPanel } from '@/components/billing/ConnectPanel';
import { PortalButton } from '@/components/billing/PortalButton';
import { toast } from 'sonner';
import { CreditCard, RefreshCw } from 'lucide-react';

export default function BillingPage() {
    usePageTitle('Billing & Payouts');
    const { currentOrganization } = useAuth();
    const { hasPermission } = usePermissions();
    const canManage = hasPermission(Permissions.ORG_BILLING_MANAGE);
    const { setHeaderTitle } = useHeaderAction();
    const [onboarding, setOnboarding] = useState(false);

    const orgId = currentOrganization?.id;

    const statusUrl = orgId ? '/billing/connect/status/org' : null;
    const accountSessionPayload = useMemo(
        () => (orgId ? { org_id: orgId } : null),
        [orgId],
    );

    const { connectState, fetchConnectStatus, stripeConnectInstance } = useConnectStatus({
        statusUrl,
        accountSessionPayload,
    });

    useEffect(() => {
        setHeaderTitle({
            icon: (
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-100 text-violet-700">
                    <CreditCard size={16} />
                </div>
            ),
            title: 'Billing & Payouts',
            subtitle: 'Connect your Stripe account to receive payouts for your published capabilities.',
        });
        return () => setHeaderTitle(null);
    }, [setHeaderTitle]);

    const handleSetUpPayments = async () => {
        if (!orgId) return;
        setOnboarding(true);
        try {
            await api.post('/billing/connect/onboard', {
                org_name: currentOrganization.name ?? 'Organization',
            });
            await fetchConnectStatus();
        } catch {
            toast.error('Failed to start Stripe onboarding');
        } finally {
            setOnboarding(false);
        }
    };

    return (
        <div className="p-6 space-y-6">
            <ConnectPanel
                connectState={connectState}
                stripeConnectInstance={stripeConnectInstance}
                fetchConnectStatus={fetchConnectStatus}
                onSetUpPayments={handleSetUpPayments}
                onboarding={onboarding}
                canManage={canManage}
                entityName="your organization"
                setupDescription="Link your organization's Stripe account to receive monthly payouts when other organizations use your published capabilities through the marketplace."
                setupFootnote="You'll complete identity verification and bank account setup inline below."
                connectedChildren={
                    <>
                        <ProviderLedgerSummary orgId={orgId} />
                        <ProviderPayoutHistory orgId={orgId} />

                        <Card>
                            <CardHeader>
                                <CardTitle>Billing Portal</CardTitle>
                                <CardDescription>
                                    Manage your payment methods, view invoices, and update billing details through the Stripe Customer Portal.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-wrap gap-3">
                                    <PortalButton label="Manage Payment Methods" />
                                    <PortalButton label="View Invoices" />
                                </div>
                            </CardContent>
                        </Card>
                    </>
                }
            />
        </div>
    );
}

// ── Org-specific sub-components ─────────────────────────────────────

interface LedgerSummary {
    total_earned: number;
    total_paid: number;
    pending_balance: number;
    currency: string;
}

interface PayoutHistoryItem {
    id: string;
    period_start: string;
    period_end: string;
    run_status: string;
    amount_cents: number;
    currency: string;
    stripe_transfer_id?: string;
    item_status: string;
    created: string;
    completed_at?: string;
}

function ProviderPayoutHistory({ orgId }: { orgId?: string }) {
    const [items, setItems] = useState<PayoutHistoryItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!orgId) return;
        setLoading(true);
        api.get<PayoutHistoryItem[]>('/billing/settlement/runs/provider?limit=10')
            .then(({ data }) => setItems(Array.isArray(data) ? data : []))
            .catch((err) => {
                console.error('BillingPage:ProviderPayoutHistory: failed to fetch payout history', err);
            })
            .finally(() => setLoading(false));
    }, [orgId]);

    if (loading) {
        return (
            <Card className="p-6">
                <div className="flex items-center gap-3">
                    <RefreshCw className="h-4 w-4 animate-spin text-slate-400" />
                    <span className="text-sm text-slate-500">Loading payout history...</span>
                </div>
            </Card>
        );
    }

    const fmt = (cents: number) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

    const statusBadge = (status: string) => {
        if (status === 'transferred') return 'bg-emerald-100 text-emerald-800';
        if (status === 'failed') return 'bg-red-100 text-red-800';
        if (status === 'skipped') return 'bg-slate-100 text-slate-600';
        return 'bg-slate-100 text-slate-600';
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>Payout History</CardTitle>
                <CardDescription>
                    Monthly settlement transfers to your Stripe Connect account.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {items.length === 0 ? (
                    <p className="text-sm text-slate-500 py-4">No payouts yet.</p>
                ) : (
                    <div className="space-y-3">
                        {items.map((item) => (
                            <div
                                key={item.id}
                                className="flex items-center justify-between rounded-lg border border-slate-200 p-3"
                            >
                                <div>
                                    <p className="text-sm font-medium text-slate-900">
                                        {new Date(item.period_start).toLocaleDateString('en-US', {
                                            month: 'short',
                                            year: 'numeric',
                                        })}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                        {item.stripe_transfer_id
                                            ? `Transfer: ${item.stripe_transfer_id.slice(0, 24)}...`
                                            : `Settlement ${item.id.slice(0, 8)}`}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-semibold text-slate-900">{fmt(item.amount_cents)}</p>
                                    <Badge variant="outline" className={statusBadge(item.item_status)}>
                                        {item.item_status}
                                    </Badge>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function ProviderLedgerSummary({ orgId }: { orgId?: string }) {
    const [ledger, setLedger] = useState<LedgerSummary | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!orgId) return;
        setLoading(true);
        api.get<LedgerSummary>('/billing/ledger/summary')
            .then(({ data }) => setLedger(data))
            .catch((err) => {
                console.error('BillingPage:ProviderLedgerSummary: failed to fetch ledger', err);
            })
            .finally(() => setLoading(false));
    }, [orgId]);

    if (loading) {
        return (
            <Card className="p-6">
                <div className="flex items-center gap-3">
                    <RefreshCw className="h-4 w-4 animate-spin text-slate-400" />
                    <span className="text-sm text-slate-500">Loading ledger summary...</span>
                </div>
            </Card>
        );
    }

    if (!ledger) return null;

    const fmt = (cents: number) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: ledger.currency || 'USD' })
            .format(cents / 100);

    return (
        <Card>
            <CardHeader>
                <CardTitle>Provider Ledger Summary</CardTitle>
                <CardDescription>Earnings from marketplace capability usage.</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="rounded-lg border border-slate-200 p-4">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Earned</p>
                        <p className="text-2xl font-semibold text-slate-900 mt-1">{fmt(ledger.total_earned)}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-4">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Paid Out</p>
                        <p className="text-2xl font-semibold text-slate-900 mt-1">{fmt(ledger.total_paid)}</p>
                    </div>
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4">
                        <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide">Pending Balance</p>
                        <p className="text-2xl font-semibold text-emerald-800 mt-1">{fmt(ledger.pending_balance)}</p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
